import { apiClient } from './apiClient';

/**
 * Async Frictionless API Service
 * Production-grade async extraction with status polling
 */

export interface ExtractMultipleAsyncRequest {
  restaurant_name: string;
  menus: Array<{
    source_url: string;
    menu_type: string;
    description: string;
    is_seasonal: boolean;
    seasonal_config?: {
      start_date: string;
      end_date: string;
    };
  }>;
  restaurant_info?: any; // Full restaurant details from search
  location?: string; // Restaurant location
}

export interface ExtractMultipleAsyncResponse {
  job_id: string;
  status: string;
  message: string;
  estimated_time_seconds: number;
  poll_endpoint: string;
}

export interface ExtractionStatusResponse {
  job_id: string;
  status: 'queued' | 'starting' | 'downloading' | 'converting' | 'extracting' | 
          'categorizing' | 'validating' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  current_step: string;
  restaurant_name: string;
  menu_count: number;
  items_extracted: number;
  items_categorized: number;
  completed_menus: Array<{
    menu_type: string;
    items_extracted: number;
    items_categorized: number;
  }>;
  failed_menus: Array<{
    menu_type: string;
    error: string;
  }>;
  errors: string[];
  created_at: string;
  updated_at: string;
  // Completion fields
  session_token?: string;
  processing_complete?: boolean;
  success?: boolean;
  total_items_extracted?: number;
  requires_auth?: boolean;
  preview_mode?: boolean;
}

class FrictionlessAsyncApi {
  private baseUrl = '/frictionless-onboarding';
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Start async extraction of multiple menus
   * Returns immediately with job_id
   */
  async startExtraction(request: ExtractMultipleAsyncRequest): Promise<ExtractMultipleAsyncResponse> {
    const response = await apiClient.post(
      `${this.baseUrl}/extract-multiple-preview-async`,
      request
    );
    return response.data;
  }

  /**
   * Get extraction job status
   */
  async getExtractionStatus(jobId: string): Promise<ExtractionStatusResponse> {
    const response = await apiClient.get(
      `${this.baseUrl}/async-status/${jobId}`
    );
    return response.data;
  }

  /**
   * Cancel extraction job
   */
  async cancelExtraction(jobId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(
      `${this.baseUrl}/cancel-extraction/${jobId}`
    );
    return response.data;
  }

  /**
   * Poll for extraction status with callback
   * Automatically stops polling when complete
   */
  async pollExtractionStatus(
    jobId: string,
    onProgress: (status: ExtractionStatusResponse) => void,
    onComplete?: (status: ExtractionStatusResponse) => void,
    onError?: (error: Error) => void,
    pollIntervalMs: number = 2000
  ): Promise<void> {
    // Clear any existing polling for this job
    this.stopPolling(jobId);

    const poll = async () => {
      try {
        const status = await this.getExtractionStatus(jobId);
        
        // Call progress callback
        onProgress(status);

        // Check if complete
        if (status.processing_complete) {
          this.stopPolling(jobId);
          
          if (status.status === 'completed' && onComplete) {
            onComplete(status);
          } else if (status.status === 'failed' && onError) {
            onError(new Error(status.errors?.join(', ') || 'Extraction failed'));
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (onError) {
          onError(error as Error);
        }
        this.stopPolling(jobId);
      }
    };

    // Start polling
    await poll(); // First call immediately
    const interval = setInterval(poll, pollIntervalMs);
    this.pollingIntervals.set(jobId, interval);
  }

  /**
   * Stop polling for a specific job
   */
  stopPolling(jobId: string): void {
    const interval = this.pollingIntervals.get(jobId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(jobId);
    }
  }

  /**
   * Stop all polling
   */
  stopAllPolling(): void {
    this.pollingIntervals.forEach((interval) => clearInterval(interval));
    this.pollingIntervals.clear();
  }

  /**
   * Convenience method: Start extraction and poll until complete
   */
  async extractWithProgress(
    request: ExtractMultipleAsyncRequest,
    onProgress: (status: ExtractionStatusResponse) => void
  ): Promise<ExtractionStatusResponse> {
    // Start extraction
    const startResponse = await this.startExtraction(request);
    console.log('🚀 Extraction started:', startResponse);

    // Return promise that resolves when extraction completes
    return new Promise((resolve, reject) => {
      this.pollExtractionStatus(
        startResponse.job_id,
        onProgress,
        (status) => resolve(status),
        (error) => reject(error)
      );
    });
  }
}

// Export singleton instance
export const frictionlessAsyncApi = new FrictionlessAsyncApi();